import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { LocationConfig, Stage } from './types/AccuLynxTypes';
import { ACCULYNX_STAGES } from './constants/AccuLynxConstants';
import { styled } from '@mui/material/styles';

const StageMappingRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const StyledFormControl = styled(FormControl)(() => ({
  backgroundColor: '#ffffff',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
  },
}));

const StyledSelect = styled(Select)(() => ({
  backgroundColor: '#ffffff',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
  },
  '& .MuiSelect-select': {
    backgroundColor: '#ffffff !important',
    minHeight: '1.4375em !important',
    padding: '8.5px 14px !important',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
  },
}));

interface StageMappingsProps {
  location: LocationConfig;
  locationIndex: number;
  isEditing: boolean;
  isLoadingStages: boolean;
  paivStages: Stage[];
  onUpdateStageMapping: (
    locationIndex: number,
    accuLynxStage: string,
    paivStageTitle: string
  ) => void;
}

export const StageMappings: React.FC<StageMappingsProps> = ({
  location,
  locationIndex,
  isEditing,
  isLoadingStages,
  paivStages,
  onUpdateStageMapping,
}) => {
  // Category-aware filter
  const filteredPaivStages: Stage[] = Array.isArray(paivStages)
    ? !location?.stageCategoryID
      ? paivStages
      : paivStages.filter(
          (s) =>
            String(s?.categoryID || '') === String(location.stageCategoryID)
        )
    : [];

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Stage Mappings
      </Typography>

      {ACCULYNX_STAGES.map((accuLynxStage) => {
        const mapping = location?.stageMappings?.find(
          (m) => m?.accuLynxStage === accuLynxStage
        ) || {
          accuLynxStage,
          paivStage: '',
          paivStageId: '',
        };

        // Resolve saved title: prefer paivStage; else lookup by paivStageId in full paivStages
        const valueTitleRaw = String(mapping?.paivStage || '').trim();
        const resolvedById = String(
          paivStages.find(
            (s) => String(s?.id || '') === String(mapping?.paivStageId || '')
          )?.title || ''
        ).trim();
        const savedTitle = valueTitleRaw || resolvedById;

        // Build options; ensure savedTitle is present even if not in filtered list
        const optionTitles = new Set(
          filteredPaivStages.map((s) => String(s?.title || '').trim())
        );
        const options: Stage[] =
          savedTitle && !optionTitles.has(savedTitle)
            ? ([{ id: savedTitle, title: savedTitle } as any] as any[]).concat(
                filteredPaivStages
              )
            : filteredPaivStages;

        return (
          <StageMappingRow
            key={`${location?.circleId || locationIndex}-${accuLynxStage}`}
          >
            <Box sx={{ width: '200px' }}>
              <Typography variant="subtitle1">{accuLynxStage}</Typography>
            </Box>

            <StyledFormControl
              sx={{ minWidth: 300 }}
              disabled={
                !isEditing || isLoadingStages || !location?.stageCategoryID
              }
            >
              <StyledSelect
                value={savedTitle || ''}
                displayEmpty
                renderValue={(selected) => {
                  const v = String(selected || '').trim();
                  if (!v) {
                    return (
                      <Box
                        sx={{
                          width: '100%',
                          color: 'text.disabled',
                          textAlign: 'left',
                        }}
                      >
                        Paiv Stage
                      </Box>
                    );
                  }
                  return v;
                }}
                onChange={(e) => {
                  const newValue = String(e.target.value).trim();
                  onUpdateStageMapping(locationIndex, accuLynxStage, newValue);
                }}
                MenuProps={{
                  PaperProps: {
                    elevation: 8,
                    sx: {
                      backgroundColor: '#ffffff',
                      '& .MuiList-root': {
                        backgroundColor: '#ffffff',
                        padding: '8px 0',
                      },
                      '& .MuiMenuItem-root': {
                        backgroundColor: '#ffffff',
                        minHeight: '48px',
                        padding: '12px 16px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                          },
                        },
                      },
                    },
                  },
                  MenuListProps: {
                    sx: {
                      padding: 0,
                      maxHeight: '300px',
                      overflowY: 'auto',
                    },
                  },
                }}
              >
                {isLoadingStages ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading stages...
                  </MenuItem>
                ) : !location?.stageCategoryID ? (
                  <MenuItem disabled>Select a CRM Category first</MenuItem>
                ) : options.length === 0 && !savedTitle ? (
                  <MenuItem disabled>No stages in this category</MenuItem>
                ) : (
                  options.map((stage: Stage) => (
                    <MenuItem key={stage.id} value={String(stage.title).trim()}>
                      {stage.title}
                    </MenuItem>
                  ))
                )}
              </StyledSelect>
            </StyledFormControl>
          </StageMappingRow>
        );
      })}
    </Box>
  );
};
